import { useEffect, useId, useRef, useState } from 'react';
import type { BilanSuppression } from '@rh/shared';
import { appel } from '../api';
import { Erreur } from './Etat';

interface Props {
  personneId: string;
  nbCandidatures: number;
  onSupprimee: () => void;
}

/**
 * Suppression définitive d'une personne (super admin), pour une demande
 * d'effacement : candidatures, documents, contacts et évaluations disparaissent,
 * statistiques comprises.
 *
 * Depuis le 2026-09-19, une seule confirmation suffit (demande de
 * l'utilisateur : « supprimer ne fonctionne pas, demander juste confirmation
 * et supprimer »). Le motif écrit et la case à cocher étaient obligatoires :
 * le bouton restait inactif tant que les deux n'étaient pas remplis, et la
 * fonction paraissait cassée. Qui a supprimé et quand reste au journal d'audit.
 */
export function SupprimerPersonne({ personneId, nbCandidatures, onSupprimee }: Props) {
  const [ouvert, setOuvert] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const bouton = useRef<HTMLButtonElement>(null);
  const annuler = useRef<HTMLButtonElement>(null);
  const titre = useId();

  const quoi = nbCandidatures > 1 ? `cette fiche et ses ${nbCandidatures} candidatures` : 'cette candidature';

  useEffect(() => {
    if (!ouvert) return;
    // Le focus part sur « Annuler » : rien d'irréversible sous la touche Entrée.
    annuler.current?.focus();
    const clavier = (evenement: KeyboardEvent) => {
      if (evenement.key !== 'Escape' || envoi) return;
      setOuvert(false);
      bouton.current?.focus();
    };
    document.addEventListener('keydown', clavier);
    return () => document.removeEventListener('keydown', clavier);
  }, [ouvert, envoi]);

  function fermer(): void {
    setOuvert(false);
    setErreur(null);
    bouton.current?.focus();
  }

  async function supprimer(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      await appel<BilanSuppression>(`/personnes/${personneId}/supprimer`, {
        method: 'POST',
        body: JSON.stringify({}),
      });
      onSupprimee();
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Suppression impossible');
      setEnvoi(false);
    }
  }

  return (
    <section className="bloc zone-danger">
      <h3>Suppression définitive</h3>
      <p className="mention">
        Pour une demande d’effacement du candidat. Sans demande, la fiche est anonymisée à
        l’échéance de conservation.
      </p>
      <button
        ref={bouton}
        type="button"
        className="bouton-second bouton-danger"
        onClick={() => setOuvert(true)}
      >
        Supprimer {quoi}
      </button>
      {erreur && !ouvert && <Erreur message={erreur} />}

      {ouvert && (
        <div className="dialogue-fond">
          <dialog open className="dialogue" aria-modal="true" aria-labelledby={titre}>
            <h2 id={titre}>Supprimer définitivement ?</h2>
            <p>
              {nbCandidatures > 1
                ? `Les ${nbCandidatures} candidatures de cette personne`
                : 'La candidature de cette personne'}
              , ses documents, ses contacts et ses évaluations seront effacés, statistiques
              comprises. Aucun retour possible.
            </p>
            {erreur && <Erreur message={erreur} />}
            <div className="actions">
              <button ref={annuler} type="button" className="bouton-second" disabled={envoi} onClick={fermer}>
                Annuler
              </button>
              <button
                type="button"
                className="bouton bouton-danger"
                disabled={envoi}
                onClick={() => void supprimer()}
              >
                {envoi ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </dialog>
        </div>
      )}
    </section>
  );
}
